import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import { useOAuth } from '@novafuturltd/core'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useQuery } from 'react-query'
import AvatarBoCan from '../../AvatarBoCan'
import AvatarPreviewDialog from '../../features/avatar-preview/AvatarPreviewDialog'
import DeclineDialog from '../../features/publish/DeclineDialog'
import GateResults from '../../features/publish/GateResults'
import { useVersionChanges } from '../../features/publish/useVersionChanges'
import VersionChangesDialog from '../../features/publish/VersionChangesDialog'
import VersionsTable from '../../features/publish/VersionsTable'
import { createAvatarBoClient } from '../../shared/AvatarBoClient'
import { AVATAR_BO_PERMISSIONS } from '../../shared/permissions'
import { baselineFor, buildPublishView } from '../../shared/publish-view'
import { shouldRetry } from '../../shared/retry'
import type { AvatarBoClient } from '../../shared/types'
import { usePublish } from './usePublish'

export interface AvatarPublishPanelProps {
  apiBaseUrl: string
  authToken?: string
  channel?: string
  client?: AvatarBoClient
}

export default function AvatarPublishPanel({
  apiBaseUrl,
  authToken,
  channel = 'prod',
  client: injected,
}: AvatarPublishPanelProps) {
  const { formatMessage } = useIntl()
  const [declining, setDeclining] = useState<number | undefined>(undefined)
  const [inspecting, setInspecting] = useState<number | undefined>(undefined)
  const [previewing, setPreviewing] = useState(false)
  const { organizationId } = useOAuth()

  const client = useMemo(
    () =>
      injected ??
      createAvatarBoClient({
        apiBaseUrl,
        authToken: () => authToken,
        // Only the preview's config fetch reads it; every BO call takes the org from the token.
        orgId: () => organizationId,
      }),
    [injected, apiBaseUrl, authToken, organizationId],
  )

  const publish = usePublish({ client, channel })
  const view = buildPublishView(publish.versions.data ?? [])

  const sets = useQuery(['avatar-bo', 'sets'], () => client.listSets(), {
    refetchOnWindowFocus: false,
    retry: shouldRetry,
  })
  const setId = sets.data?.[0]?.id
  const baseline =
    inspecting === undefined ? undefined : baselineFor(view.rows, inspecting)
  const inspected = useVersionChanges({
    client,
    setId,
    version: inspecting,
    baseline,
  })

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h5" component="h1">
          {formatMessage({ id: 'avatarStudio.publish.title' })}
        </Typography>

        <Alert severity="info">
          {formatMessage({ id: 'avatarStudio.notice.publishIsImmediate' })}
        </Alert>

        {view.hasNeverPublished && !publish.versions.isLoading && (
          <Alert severity="warning">
            {formatMessage({ id: 'avatarStudio.publish.nothingLive' })}
          </Alert>
        )}

        {publish.error && <Alert severity="error">{publish.error}</Alert>}
        <GateResults issues={publish.gateIssues} />

        <Stack direction="row" justifyContent="flex-end">
          <AvatarBoCan
            perform={AVATAR_BO_PERMISSIONS.catalogueWrite}
            // tslint:disable-next-line: jsx-no-lambda
            yes={() => (
              <Button
                variant="contained"
                disabled={publish.busy}
                onClick={() => publish.stage.mutate()}
              >
                {formatMessage({
                  id: publish.stage.isLoading
                    ? 'avatarStudio.publish.staging'
                    : 'avatarStudio.publish.stage',
                })}
              </Button>
            )}
          />
        </Stack>

        <VersionsTable
          rows={view.rows}
          loading={publish.versions.isLoading}
          busy={publish.busy}
          onApprove={(n) => publish.approve.mutate(n)}
          onDecline={(n) => setDeclining(n)}
          onPublish={(n) => publish.publish.mutate(n)}
          onInspect={(n) => setInspecting(n)}
        />

        <DeclineDialog
          version={declining}
          busy={publish.busy}
          onCancel={() => setDeclining(undefined)}
          onConfirm={(note) => {
            publish.decline.mutate({ n: declining as number, note })
            setDeclining(undefined)
          }}
        />

        {inspecting !== undefined && (
          <VersionChangesDialog
            open={!previewing}
            version={inspecting}
            baseline={baseline?.n}
            baselineIsLive={baseline?.isLive}
            changes={inspected.changes}
            loading={inspected.isLoading}
            error={inspected.error}
            onClose={() => setInspecting(undefined)}
            onPreview={
              inspected.catalogue ? () => setPreviewing(true) : undefined
            }
          />
        )}

        {inspected.catalogue && (
          <AvatarPreviewDialog
            open={previewing}
            catalogue={inspected.catalogue}
            changes={inspected.changes}
            onClose={() => setPreviewing(false)}
          />
        )}
      </Stack>
    </Box>
  )
}
